// eslint-disable-next-line
import React, { useState } from 'react';
import './Login.css';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Lock, Smartphone, Mail } from 'lucide-react';
import LogoImage from '../../../assets/Admin/AdminNavbar/logo-image.png';
import { useAuth } from '../../../context/AuthContext';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  trimString,
  isValidEmail,
  sanitizePhoneDigits,
  isValidPhoneNumber,
} from '../../../components/Validation/Validation';
import CountryCodeSelect from '../../../components/CountryCodeSelect/CountryCodeSelect';
import { DEFAULT_DIAL_CODE, looksLikePhoneInput, toE164 } from '../../../utils/countryCodes';

const Login = () => {
  const [auth, setAuth] = useAuth();
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpDialCode, setOtpDialCode] = useState(DEFAULT_DIAL_CODE);
  const [showPassword, setShowPassword] = useState(false);
  const [validationEmailPassword, setValidationEmailPassword] = useState(false);
  const navigate = useNavigate();

  const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

  // Validation states
  const isEmailPasswordValid = userEmail.trim() !== '' && password.trim() !== '';
  const isOtpValid = otpEmail.trim() !== '';
  // The OTP field takes either an email or a phone number; only show the
  // country code selector once what's typed can only be a phone number.
  const isOtpPhoneEntry = looksLikePhoneInput(otpEmail);

  const handleUserEmailChange = e => {
    setUserEmail(e.target.value);
    setValidationEmailPassword(false);
  };
  const handleUserPasswordChange = e => {
    setPassword(e.target.value);
    setValidationEmailPassword(false);
  };
  const handleOtpEmailChange = e => {
    setOtpEmail(e.target.value);
  };
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const handleSubmit = async e => {
    e.preventDefault();

    if (!isEmailPasswordValid) {
      toast.dismiss();
      toast.error('Please fill in all required fields');
      return;
    }

    const emailToUse = trimString(userEmail).toLowerCase();

    if (!isValidEmail(emailToUse)) {
      toast.dismiss();
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      const { data } = await axios.post(
        `${SERVER_API_URL}/auth/jwt/login`,
        {
          email: emailToUse,
          password: password,
        },
        {
          withCredentials: true,
        }
      );
      if (data) {
        const { id, user_type, access_token, first_name, last_name, email, profile_image } = data;

        // Never store a session for a non-admin: this is the admin app, so
        // anything else is rejected before any credential touches the browser
        if (user_type !== 'admin') {
          toast.dismiss();
          toast.error('Not Authorized');
          return;
        }

        setAuth({
          ...auth,
          id,
          user_type,
          access_token,
          first_name,
          last_name,
          email,
          profile_image,
        });
        Cookies.set('access_token', access_token);
        Cookies.set('user_type', user_type);
        Cookies.set('first_name', first_name);
        Cookies.set('last_name', last_name);
        Cookies.set('email', email);
        Cookies.set('profile_image', profile_image);

        try {
          const adminResponse = await axios.get(`${SERVER_API_URL}/admin/rbac/admin/me`, {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
            withCredentials: true,
          });

          const adminData = adminResponse.data;
          const roles = adminData.roles || [];

          let redirectPath = '/admin/dashboard';

          if (roles.includes('operations_officer') || roles.includes('organisation_manager')) {
            redirectPath = '/admin/organisation';
          } else if (
            roles.includes('talent_manager') ||
            roles.includes('user_onboarding') ||
            roles.includes('support_associate')
          ) {
            redirectPath = '/admin/users/instructor';
          } else if (
            roles.includes('finance_officer') ||
            roles.includes('admin') ||
            roles.includes('super_admin')
          ) {
            redirectPath = '/admin/dashboard';
          }

          toast.dismiss();
          toast.success('Login successful');
          navigate(redirectPath);
        } catch (adminError) {
          console.error('Error fetching admin details:', adminError);
          toast.dismiss();
          toast.success('Login successful');
          navigate('/admin/dashboard');
        }
      }
    } catch (error) {
      // The API rejects a non-admin signing in here with 403; show why rather
      // than the generic wrong-credentials message
      if (error.response?.status === 403) {
        toast.dismiss();
        toast.error(error.response?.data?.detail || 'Not Authorized');
        return;
      }
      console.log(error);
      setValidationEmailPassword(true);
    }
  };

  const handleOtpLogin = async e => {
    if (e) e.preventDefault();

    if (!isOtpValid) {
      toast.dismiss();
      toast.error('Please enter your email address or phone number');
      return;
    }

    const input = trimString(otpEmail);
    if (!input) {
      toast.dismiss();
      toast.error('Please enter your email address or phone number');
      return;
    }

    const isEmail = isValidEmail(input);

    // Prepare request body based on input type
    let requestBody;
    if (isEmail) {
      requestBody = { email: input.toLowerCase() };
    } else {
      const phoneDigits = sanitizePhoneDigits(input);
      if (!isValidPhoneNumber(phoneDigits)) {
        toast.dismiss();
        toast.error('Please enter a valid phone number');
        return;
      }
      // Phone numbers are stored in E.164 form ("+919072824973"), so the
      // country code has to travel with the number
      requestBody = { phone_no: toE164(input, otpDialCode) };
    }

    try {
      const response = await axios.post(`${SERVER_API_URL}/auth/otp-login`, requestBody, {
        withCredentials: true,
      });

      // A 200 is the success signal; don't depend on the body, which some
      // send paths return empty
      if (response.status === 200) {
        toast.dismiss();
        toast.success('OTP sent successfully!');
        navigate('/verify-otp', {
          state: { email: isEmail ? input : requestBody.phone_no, dialCode: otpDialCode },
        });
      } else {
        toast.dismiss();
        toast.error(response.data?.detail || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.dismiss();
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
  };

  return (
    <Box className="login-container">
      {/* Header Section */}
      <Box className="login-header">
        <Box className="login-logo">
          <img src={LogoImage} alt="Klass Online Logo" className="logo-image" />
        </Box>
      </Box>

      {/* Main Content - Two Cards */}
      <Box className="login-cards-container">
        {/* Left Card - Email & Password */}
        <Box className="login-card">
          <Box className="card-header">
            <Lock className="card-icon email-password-icon" />
            <Typography component="h3" className="card-title">
              Sign in with Email & Password
            </Typography>
          </Box>

          <form className="card-form" onSubmit={e => handleSubmit(e)}>
            <Box className="input-group">
              <label htmlFor="email" className="input-label">
                Email
              </label>
              <Box className="input-container">
                <Mail className="input-icon" />
                <input
                  id="email"
                  className="input-field"
                  type="email"
                  placeholder="admin@example.com"
                  value={userEmail}
                  onChange={handleUserEmailChange}
                  required
                />
              </Box>
            </Box>

            <Box className="input-group">
              <label htmlFor="password" className="input-label">
                Password
              </label>
              <Box className="input-container">
                <Lock className="input-icon" />
                <input
                  id="password"
                  className="input-field"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handleUserPasswordChange}
                  required
                />
                <div
                  onClick={handleClickShowPassword}
                  className="password-toggle"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? (
                    <VisibilityOff className="toggle-icon" />
                  ) : (
                    <Visibility className="toggle-icon" />
                  )}
                </div>
              </Box>
              {validationEmailPassword && (
                <div className="error-message">Incorrect Email or Password</div>
              )}
            </Box>

            <Box className="forgot-password-container">
              <Link to="/forgot-password" className="forgot-password-link">
                Forgot Password?
              </Link>
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isEmailPasswordValid ? 'disabled' : ''}`}
              disabled={!isEmailPasswordValid}
            >
              Sign In
            </button>
          </form>
        </Box>

        {/* Right Card - OTP Login */}
        <Box className="login-card">
          <Box className="card-header">
            <Box className="otp-icon-container">
              <Smartphone className="card-icon otp-icon" />
            </Box>
            <Typography component="h3" className="card-title">
              Or Sign in with OTP
            </Typography>
          </Box>

          <Typography variant="body2" className="card-description">
            We'll send a one-time password to your registered email or phone number.
          </Typography>

          <form className="card-form" onSubmit={handleOtpLogin}>
            <Box className="input-group">
              <label htmlFor="otp-email" className="input-label">
                Email or Phone Number
              </label>
              <Box className="phone-input-row">
                {isOtpPhoneEntry && (
                  <CountryCodeSelect value={otpDialCode} onChange={setOtpDialCode} />
                )}
                <Box className="input-container">
                  {isOtpPhoneEntry ? (
                    <Smartphone className="input-icon" />
                  ) : (
                    <Mail className="input-icon" />
                  )}
                  <input
                    id="otp-email"
                    className="input-field"
                    type="text"
                    placeholder="Enter email or phone"
                    value={otpEmail}
                    onChange={handleOtpEmailChange}
                    required
                  />
                </Box>
              </Box>
            </Box>

            <button
              type="submit"
              className={`primary-button ${!isOtpValid ? 'disabled' : ''}`}
              disabled={!isOtpValid}
            >
              Send OTP
            </button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
